import NimSDK, { NIM } from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM';
import {
  NIMMessage,
  NIMSendCustomMsgOptions,
} from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM/MessageInterface';
import { version } from '@yxim/nim-web-sdk/package.json';
import { NIMSystemMessage } from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM/SystemMessageInterface';
import {
  NIM_LOGIN_SIGNAL,
  InstanceOptions,
  NimSystemACKMessage,
  NimSystemHealthACKMessage,
  NimSytemAssignFailMessage,
  NimSytemUnreadCountMessage,
  NimSystemReadEventMessage,
  NimSystemFollowerEventMessage,
  NimSystemAIReplyMessage,
  NimSystemOfficialReadMarkMessage,
  NimSystemAssistantReadMarkMessage,
  NimSystemRemindSceneMessage,
  NimSystemRemindSolveMessage,
  NimSystemStoreTransferMessage,
  NimSystemPayMessage,
  NimSystemEventStreamMessage,
  NimSystemRemoveTalkControlMessage,
  NimSystemBatchTransferMessage,
  NimSystemSyncTiktokTradeUidMessage,
  NimSystemSendMsgReqMessage,
  NimSystemACKTimeoutMessage,
  NimSystemExportFileMessage,
  NimSystemTikTokShopOfflineMessage,
  NimSystemUpdateMarkMessage,
  NimSystemAuthSuccessMessage,
  NimSystemKnowledgeQueryMessage,
  NimSystemKnowledgeQueryClearMessage,
} from './eventBus';
import nimitt from './nimitt';
import beginHearbeat, { stopHeartbeat } from 'services/heartBeat';
import { NimSystemBasicMessage, emit } from './eventBus';
import { nimEventReport } from 'components/Nim/nimReport';
import nimMsgContentParser from './nimMsgContentParser';
import getNimCacheId from 'hooks/business/im/getNimCacheId';
import wait from 'utils/wait';
import { offlineNotify } from './offlineNotify';
import { on } from './eventBus';
import { promisify } from './promisify';
import { isArray, isNil, isString, toUpper } from 'lodash-es';
import { NIMSession } from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM/SessionInterface';
import { uploadLogFiles, writeLog } from './logger';
import logout from 'utils/logout';
import { NIMOnLoginPortsChangeResult } from '@yxim/nim-web-sdk/dist/types/nim/types';

const NIM_ENV = import.meta.env.VITE_NIM_ENV as 'nta' | 'sth' | 'dew' | 'stq' | 'prq';

let __instance__: NIM | null = null;
let lastDisconnectTimestamp = 0;
// ack timeout map
const ackTimeoutTaskMap: Record<
  NimSystemACKMessage['content']['yunXinMsgId'],
  {
    timeout: NodeJS.Timeout;
    status: 'ack' | 'timeover' | 'pending';
    sentTimeMs: number;
  }
> = {};

// 云信消息监听
function onmsg(data: NIMMessage) {
  const content = nimMsgContentParser<Conversation.Message>(data);
  if (content) {
    if (data?.idServer) {
      const nimid = getNimCacheId(data.idServer);
      content.yunXinMsgId = data.idServer;
      content.msgId = content.msgId || nimid;
    }
    emit('message', content);
  }
}

// 云信离线消息监听
function onofflinemsgs(data: { msgs: NIMMessage[] }) {
  const notDisconnected = lastDisconnectTimestamp === 0;

  // eslint-disable-next-line no-console
  console.log('offlinemsg::', data);

  if (notDisconnected) {
    const msgLength = data?.msgs?.filter?.((msg) => msg.content)?.length;

    if (msgLength === 0) {
      return;
    }

    offlineNotify(msgLength);

    return;
  }

  const msgs: Conversation.Message[] = [];
  const msgIdToIdServer: Record<string, string> = {};
  data?.msgs?.forEach?.((msg: NIMMessage) => {
    const content: Conversation.Message = nimMsgContentParser<any>(msg);
    if (content) {
      if (!content.msgFromType) {
        return;
      }
      msgs.push(content);
      if (msg.idServer) {
        msgIdToIdServer[content.msgId] = msg.idServer;
      }
    }
  });

  msgs.sort((m1, m2) => parseInt(m1.sendTime as string, 10) - parseInt(m2.sendTime as string, 10));
  (async () => {
    await wait(1800);
    for (let i = 0; i < msgs.length; i += 1) {
      const content = msgs[i];
      const idServer = msgIdToIdServer[content.msgId];
      if (idServer) {
        const nimid = getNimCacheId(idServer);
        content.yunXinMsgId = idServer;
        content.msgId = content.msgId || nimid;
      }
      emit('message', content);
      await wait(800);
    }
  })();
}

// 自定义系统消息监听
function oncustomsysmsg(data: NIMSystemMessage) {
  if (data && data.content === 'pull_notice_msg') {
    emit('sys:message:pull_notice_msg', { type: 'PULL_NOTICE_MSG', content: null });

    return;
  }

  if (data && data.content === 'reconnectingKicked') {
    __instance__?.disconnect({
      done: () => __instance__?.connect(),
    });
    return;
  }

  const content = nimMsgContentParser<NimSystemBasicMessage>(data);

  if (!content) {
    return;
  }
  if (data?.from === 'health') {
    emit('sys:message:health_ack', content as NimSystemHealthACKMessage);
    return;
  }

  const { type: sysType } = content;
  const type = toUpper(sysType);
  if (!type) {
    return;
  }

  if (type === 'ACK') {
    emit('sys:message:ack', content as NimSystemACKMessage);
    const {
      content: { yunXinMsgId },
    } = content as NimSystemACKMessage;
    if (ackTimeoutTaskMap[yunXinMsgId]) {
      clearTimeout(ackTimeoutTaskMap[yunXinMsgId].timeout);
      if (ackTimeoutTaskMap[yunXinMsgId].status === 'timeover') {
        const { sentTimeMs } = ackTimeoutTaskMap[yunXinMsgId];
        const delayMS = new Date().valueOf() - sentTimeMs;
        // 若本地超时后再收到ack上报到sentry
        nimEventReport('sys:ack:after:timeout', {
          delayMS,
          sentTimeMs,
          content,
        });
      }
      delete ackTimeoutTaskMap[yunXinMsgId];
    }
  } else if (type === 'ASSIGN_FAIL') {
    emit('sys:message:assign_fail', content as NimSytemAssignFailMessage);
  } else if (type === 'UNREAD_COUNT') {
    emit('sys:message:unread_count', content as NimSytemUnreadCountMessage);
  } else if (type === 'READ_EVENT') {
    emit('sys:message:read_event', content as NimSystemReadEventMessage);
  } else if (type === 'FOLLOWER_EVENT') {
    emit('sys:message:follower_event', content as NimSystemFollowerEventMessage);
  } else if (type === 'AI_REPLY') {
    emit('sys:message:ai_reply', content as NimSystemAIReplyMessage);
  } else if (type === 'OFFICIAL_READ_MARK') {
    emit('sys:message:official_read_mark', content as NimSystemOfficialReadMarkMessage);
  } else if (type === 'ASSISTANT_READ_MARK') {
    emit('sys:message:assistant_read_mark', content as NimSystemAssistantReadMarkMessage);
  } else if (type === 'REMIND_SCENE') {
    emit('sys:message:remind_scene', content as NimSystemRemindSceneMessage);
  } else if (type === 'REMIND_SOLVE') {
    emit('sys:message:remind_solve', content as NimSystemRemindSolveMessage);
  } else if (type === 'STORE_TRANSFER') {
    emit('sys:message:store_transfer', content as NimSystemStoreTransferMessage);
  } else if (type === 'PAY') {
    emit('sys:message:pay', content as NimSystemPayMessage);
  } else if (type === 'MESSAGE_EVENT_STREAM') {
    emit('sys:message:message_event_stream', content as NimSystemEventStreamMessage);
  } else if (type === 'REMOVE_TALK_CONTROL') {
    emit('sys:message:remove_talk_control', content as NimSystemRemoveTalkControlMessage);
  } else if (type === 'BATCH_TRANSFER') {
    emit('sys:message:batch_transfer', content as NimSystemBatchTransferMessage);
  } else if (type === 'SYNC_TIKTOK_TRADE_UID') {
    emit('sys:message:sync_tiktok_trade_uid', content as NimSystemSyncTiktokTradeUidMessage);
  } else if (type === 'SEND_MSG_REQ') {
    emit('sys:message:send_msg_req', content as NimSystemSendMsgReqMessage);
  } else if (type === 'BOT_SENT_LIMIT_REACH') {
    emit('sys:message:bot_sent_limit_reach', content as NimSystemBasicMessage);
  } else if (type === 'ASSIGN_CONVERSATION_STORES_URL') {
    emit('sys:message:assign_conversation_stores_url', content as NimSystemBasicMessage);
  } else if (type === 'SYNC_NOTICE_UNREAD_COUNT') {
    emit('sys:message:sync_notice_unread_count', content as NimSystemBasicMessage);
  } else if (type === 'EXPORT_FILE') {
    emit('sys:message:export_file', content as NimSystemExportFileMessage);
  } else if (type === 'SYNC_SHOP_INFO') {
    emit('sys:message:sync_shop_info', content as NimSystemBasicMessage);
  } else if (type === 'TIKTOK_SHOP_OFFLINE') {
    emit('sys:message:tiktok_shop_offline', content as NimSystemTikTokShopOfflineMessage);
  } else if (type === 'UPDATE_MARK') {
    emit('sys:message:update_mark', content as NimSystemUpdateMarkMessage);
  } else if (type === 'SYNC_ORDER_BALANCE') {
    emit('sys:message:sync_order_balance', content as NimSystemBasicMessage);
  } else if (type === 'AUTH_SUCCESS') {
    emit('sys:message:auth_success', content as NimSystemAuthSuccessMessage);
  } else if (type === 'KNOWLEDGE_QUERY_CLEAR') {
    try {
      content.content = JSON.parse(content.content);
      emit('sys:message:knowledge_query_clear', content as NimSystemKnowledgeQueryClearMessage);
    } catch (err) {
      console.log('[Nim]sys:message:knowledge_query_clear', err);
    }
  } else if (type === 'KNOWLEDGE_QUERY') {
    try {
      content.content = JSON.parse(content.content);
      emit('sys:message:knowledge_query', content as NimSystemKnowledgeQueryMessage);
    } catch (err) {
      console.log('[Nim]sys:message:knowledge_query', err);
    }
  }
}

// 自定义系统离线消息
function onofflinecustomsysmsgs(datas: NIMSystemMessage[]) {
  const notDisconnected = lastDisconnectTimestamp === 0;

  if (notDisconnected) {
    return;
  }

  datas.forEach((msg) => {
    oncustomsysmsg(msg);
  });
}

/**
 * 登录云信帐号.
 * NIM.getInstance接口为单例模式, 对于同一个帐号, 永远返回同一份实例, 即只有第一次调用会初始化一个实例,
 * 后续调用此接口会直接返回初始化过的实例, 同时也会调用接口更新 IM 配置更新传入的配置.
 * 每个帐号对应一个独立的`websocket`通道.
 * 后续调用此接口时, 如果连接已断开, 会自动建立连接, 当发生掉线时，SDK 会自动进行重连.
 * https://doc.yunxin.163.com/docs/TM5MzM5Njk/zE0NDY4Njc?platformId=60179
 */
function initInstance(instanceOptions: InstanceOptions, version: string = '8.6.2') {
  const { orgId, userId, ...options } = instanceOptions;
  const logLevel = options.logLevel;
  const defaultLink = options.defaultLink;
  const logOpened = isString(logLevel) && logLevel.length > 0;
  const accid = options.account;

  let isReconnecting = false;
  let webConnectDevice: NIMOnLoginPortsChangeResult | undefined = undefined;

  __instance__ = NimSDK.getInstance({
    ...options,
    logFunc: logOpened
      ? ({
          log: writeLog,
          warn: writeLog,
          info: writeLog,
          debug: writeLog,
          error: writeLog,
        } as any)
      : undefined,
    onconnect: (data) => {
      console.log('onconnect', data, webConnectDevice, isReconnecting);
      nimEventReport(
        'connect',
        {
          ...data,
          webConnectDevice: webConnectDevice ? JSON.stringify(webConnectDevice) : '',
          isReconnecting: isReconnecting ? 'true' : 'false',
          accid,
          logLevel,
          defaultLink,
        },
        version,
      );
      if (webConnectDevice) {
        if (isReconnecting) {
          sendSystem({
            scene: 'p2p',
            to: webConnectDevice.account,
            sendToOnlineUsersOnly: true,
            cc: true,
            content: 'reconnectingKicked',
          });
          logout('kicked');
          return;
        } else {
          nimEventReport(
            'manual_kick',
            {
              ...data,
              accid,
              logLevel,
              defaultLink,
            },
            version,
          );
          __instance__?.kick({
            deviceIds: [webConnectDevice.deviceId] as any,
          });
        }
      }
      isReconnecting = false;
      webConnectDevice = undefined;
      emit('connect', data);
    },
    ondisconnect: (data) => {
      console.log('ondisconnect', data);
      lastDisconnectTimestamp = Date.now();
      emit('disconnect', data);
      if (data?.code === 'kicked') {
        nimEventReport(
          'kicked',
          {
            ...data,
            accid,
            logLevel,
            defaultLink,
          },
          version,
        );
      } else if (data?.code === 'logout') {
        nimEventReport(
          'logout',
          {
            ...data,
            accid,
            logLevel,
            defaultLink,
          },
          version,
        );
      } else if (typeof data?.code === 'number') {
        nimEventReport(
          'accountIssue',
          {
            ...data,
            accid,
            logLevel,
            defaultLink,
          },
          version,
        );
      } else {
        nimEventReport(
          'disconnect',
          {
            ...data,
            accid,
            logLevel,
            defaultLink,
          },
          version,
        );
        if (logOpened) {
          uploadLogFiles({
            accid,
            orgId,
            userId,
          });
        }
      }
      isReconnecting = false;
    },
    onwillreconnect: (data) => {
      console.log('onwillreconnect', data);
      isReconnecting = true;
      emit('willReconnect', data);
      nimEventReport('willReconnect', { ...data, accid, logLevel, defaultLink }, version);
    },
    onerror: (data) => {
      console.log('onerror', data);
      lastDisconnectTimestamp = Date.now();
      emit('error', data);
      nimEventReport('error', { ...data, accid, logLevel, defaultLink }, version);
      if (logOpened) {
        uploadLogFiles({
          accid,
          orgId,
          userId,
        });
      }
    },
    onloginportschange: (data) => {
      console.log('onloginportschange', data);
      const webDevice = data.find((item) => item.type.toLowerCase() === 'web' && item.online);
      webConnectDevice = webDevice;
      nimEventReport(
        'loginPortsChange',
        {
          webConnectDevice: webConnectDevice ? JSON.stringify(webConnectDevice) : '',
          accid,
          logLevel,
          defaultLink,
        },
        version,
      );
    },
    onmsg,
    onofflinemsgs,
    oncustomsysmsg,
    onofflinecustomsysmsgs,
  });
  // @ts-ignore
  window.__instance__ = __instance__;
  // 避免重复 init 导致 worker 堆积
  stopHeartbeat();
  beginHearbeat(__instance__);
}

const login = (arg: InstanceOptions) => {
  nimitt.emit(NIM_LOGIN_SIGNAL, arg);
};

nimitt.on(NIM_LOGIN_SIGNAL, (p) => {
  initInstance(p as InstanceOptions, version);
});

// 发送消息接口
const send = async (param: NIMSendCustomMsgOptions) => {
  const { to, scene, content } = param;
  const resp = await promisify(
    __instance__,
    'sendCustomMsg',
  )({
    scene,
    to,
    content: JSON.stringify(content),
    env: NIM_ENV,
  });
  const idServer = resp?.idServer;
  if (idServer) {
    //  handle with ack timeout
    const now = new Date().valueOf();
    const timeout = setTimeout(() => {
      if (ackTimeoutTaskMap[idServer]?.status !== 'pending') {
        return;
      }
      ackTimeoutTaskMap[idServer].status = 'timeover';
      emit('sys:message:ack_timeout', {
        type: 'ACK_TIMEOUT',
        content: {
          yunXinMsgId: idServer,
          message: 'timeout',
        },
      } as NimSystemACKTimeoutMessage);
      nimEventReport('nim:local:message:ack:timeout', {
        yunXinMsgId: idServer,
      });

      // @qingsong.ding 10秒必超時
    }, 15 * 1000);
    ackTimeoutTaskMap[idServer] = {
      timeout,
      status: 'pending',
      sentTimeMs: now,
    };
  }
  return resp;
};

// 发送系统消息
export const sendSystem = (
  params: Omit<Parameters<NIM['sendCustomSysMsg']>[0], 'done' | 'env'>,
) => {
  return promisify(__instance__, 'sendCustomSysMsg')({ ...params, env: NIM_ENV });
};

const nimInst = {
  on,
  send,
  sendSystem,
  login,
  forceReconnect: () => {
    __instance__?.disconnect({
      done: () => {
        setTimeout(() => {
          __instance__?.connect?.();
          /**
           * 根据官方文档，disconnect、logout以后最好等待3秒以上再执行connect
           */
        }, 3000);
      },
    });
  },
  logout: () => {
    if (!__instance__) {
      return Promise.resolve(true);
    }
    // 退出/销毁 IM 实例时清理心跳 worker，避免长时间运行累积
    stopHeartbeat();
    return new Promise((resolve) => {
      __instance__?.destroy?.({
        done: () => {
          __instance__ = null;
          resolve(true);
        },
      });
    });
  },
};

export const deleteLocalSession = (id: string | string[]) => {
  if (isNil(id) || !isString(id) || !isArray(id) || id?.length === 0) return;

  if (isArray(id)) {
    const params = id.filter((id) => !!id && isString(id));
    __instance__?.deleteLocalSession({
      id: params as any,
    });
  } else {
    __instance__?.deleteLocalSession({
      id: id as any,
    });
  }
};

export const getLocalSessions = (done: (data: NIMSession[]) => void) => {
  __instance__?.getLocalSessions({
    reverse: true,
    done: (err, data: any) => {
      if (!err) {
        done(data?.sessions as NIMSession[]);
      }
    },
  });
};

function debugHandler(...args: any) {
  // eslint-disable-next-line no-console
  console.log('nim debug::::', args);
}

function addDebug() {
  nimitt.on('*', debugHandler);
}

function removeDebug() {
  nimitt.off('*', debugHandler);
}

if (localStorage.getItem('__NIM_DEBUG__') === 'true') {
  addDebug();
} else {
  removeDebug();
}

if (import.meta.env.DEV) {
  addDebug();
}

export default nimInst;

export type NimInst = typeof nimInst;
